import * as THREE from 'three';
import { GameOptions, NetworkManager } from '../managers/NetworkManager';
import { CameraController } from '../controllers/CameraController';
import { eventBus } from '../events/EventBus';

// Import component managers
import { CourtManager } from '../managers/CourtManager';
import { PlayerManager } from '../managers/PlayerManager';
import { BallManager } from '../managers/BallManager';
import { DebugManager } from '../managers/DebugManager';
import { UIManager } from '../managers/UIManager';
import { Player } from '../models/Player';
import { Debug } from '@shared/Debug';
import { soundManager } from '../audio/SoundManager';

/**
 * Main game scene that coordinates all game components
 */
export class GameScene {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private networkManager: NetworkManager;
  private cameraController: CameraController | null = null;
  private domElement: HTMLElement;
  private renderer: THREE.WebGLRenderer;
  private _lastUpdateTime: number = 0;
  private isDribbling: boolean = false;

  // Component managers
  private courtManager: CourtManager;
  private playerManager: PlayerManager;
  private ballManager: BallManager;
  private debugManager: DebugManager;
  private uiManager: UIManager;

  constructor(scene: THREE.Scene, camera: THREE.PerspectiveCamera, networkManager: NetworkManager, renderer: THREE.WebGLRenderer) {
    this.scene = scene;
    this.camera = camera;
    this.networkManager = networkManager;
    this.renderer = renderer;
    this.domElement = renderer.domElement;
    
    
    // Create component managers
    this.courtManager = new CourtManager(this.scene);
    this.playerManager = new PlayerManager(this.scene, this.networkManager, this.courtManager);
    this.ballManager = new BallManager(this.scene);
    this.debugManager = new DebugManager(this.scene);
    this.uiManager = new UIManager();
    
    // Initialize the scene
    this.initialize();
  }
  
  /**
   * Initialize the game scene and all components
   */
  private initialize(): void {
    // Create basic scene lighting
    this.setupCamera();
    
    // Set up court manager
    this.courtManager.initialize();
    
    // Set up ball manager
    this.ballManager.initialize();
    
    // Set up debug manager
    this.debugManager.initialize();
    
    // Set up UI manager
    this.uiManager.initialize();
    
    // Initialize network callbacks
    this.initNetworkCallbacks();
    
    // Register player manager callbacks with the network manager
    this.playerManager.registerNetworkCallbacks();
    
    // Update room ID display
    this.uiManager.updateRoomId(this.networkManager.getRoomId());
    
    // Initialize camera controller (after scene is fully set up)
    this.cameraController = new CameraController(this.camera, this.domElement, this);
    
    // Set up additional scene event handling
    this.setupEventHandling();

    soundManager.play('crowd', 0.20);

    // Log camera control instructions
    Debug.info('Camera Controls:');
    Debug.info('- Press C to cycle camera modes');
    Debug.info('- Press F to toggle first-person mode');
    Debug.info('- Press P to toggle player following mode');
    Debug.info('- Press R to reset camera position');
    Debug.info('- Press O to toggle orbit controls (disabled by default)');
    Debug.info('- Press D to toggle debug visualizations (disabled by default)');
    Debug.info('- Press B to return ball to your hand if available');
  }
  
  /**
   * Set up camera
   */
  private setupCamera(): void {

    this.camera.position.set(0, 30, 50); // Position to the side of the court
    this.camera.up.set(0, 1, 0); // Standard up vector
    this.camera.lookAt(0, 1, 0); // Look slightly above center court
    
    // Debug settings
    this.camera.fov = 15;
    this.camera.updateProjectionMatrix();
  }
  
  /**
   * Initialize network callbacks
   */
  private initNetworkCallbacks(): void {
    // Add callback for when the game state changes
    this.networkManager.onStateChange((state) => {
      this.updateGameState(state);
    });
    
    // Add callback for basket events
    this.networkManager.onBasket((message) => {
      eventBus.publish('game:basket', message);
    });
    
    // Add handlers for game state changes
    this.networkManager.onGameStarted(() => {
      eventBus.publish('game:started');
    });
    
    this.networkManager.onGamePaused(() => {
      eventBus.publish('game:paused');
    });
    
    this.networkManager.onGameOver((data) => {
      eventBus.publish('game:over', data);
    });
  }
  
  /**
   * Set up additional event handling
   */
  private setupEventHandling(): void {
    // Handle court events
    eventBus.subscribe('court:request-basket-positions', () => {
      const hoopAPosition = this.courtManager.getHoopAPosition();
      const hoopBPosition = this.courtManager.getHoopBPosition();
      
      eventBus.publish('court:basket-positions', {
        hoopAPosition,
        hoopBPosition
      });
    });
    
    // Handle player-ball interaction events
    eventBus.subscribe('player:ball-update', (data: { playerId: string, ball: any, hasBall: boolean }) => {
      this.playerManager.setPlayerHasBall(data.playerId, data.hasBall, data.ball);
    });
    
    // Handle court-net animation events
    eventBus.subscribe('court:trigger-net', (data: { basketId: 'A' | 'B' }) => {
      this.courtManager.triggerNetAnimation(data.basketId);
    });
  }
  
  /**
   * Update the game state based on network messages
   */
  // Track the last update time for each component
  private lastPlayerUpdateTime: number = 0;
  private lastBallUpdateTime: number = 0;
  private lastRigidBodyUpdateTime: number = 0;
  private lastUIUpdateTime: number = 0;
  
  // Update rate constants (ms)
  private readonly PLAYER_UPDATE_RATE = 16; // ~60fps
  private readonly BALL_UPDATE_RATE = 16;   // ~60fps
  private readonly RIGIDBODY_UPDATE_RATE = 100; // ~10fps (debug items)
  private readonly UI_UPDATE_RATE = 250;    // ~4fps (score, timer)
  
  /**
   * Update the game state with throttling for different components
   * This prevents unnecessary updates for components that don't need high frequency updates
   */
  private updateGameState(state: any): void {
    const now = performance.now();
    
    // Update player positions with throttling
    if (state.players && (now - this.lastPlayerUpdateTime >= this.PLAYER_UPDATE_RATE)) {
      this.playerManager.updatePlayers(state.players);
      this.lastPlayerUpdateTime = now;
    }
    
    // Update ball position and physics with throttling
    if (state.ball && (now - this.lastBallUpdateTime >= this.BALL_UPDATE_RATE)) {
      this.ballManager.updateBall(state.ball, this.anyPlayerHasBall());
      this.lastBallUpdateTime = now;
    }
    
    // Update rigid bodies with lower frequency (debug visualization)
    if (state.rigidBodies && (now - this.lastRigidBodyUpdateTime >= this.RIGIDBODY_UPDATE_RATE)) {
      this.debugManager.updateRigidBodies(state.rigidBodies);
      this.lastRigidBodyUpdateTime = now;
    }
    
    // Update UI elements with the lowest frequency
    if (now - this.lastUIUpdateTime >= this.UI_UPDATE_RATE) {
      this.uiManager.updateFromGameState(state);
      this.lastUIUpdateTime = now;
    }
  }
  
  /**
   * Update method called once per frame
   * @param deltaTime Time elapsed since last frame in seconds
   */
  public update(deltaTime: number): void {
    // Update all component managers
    this.courtManager.update(deltaTime);
    this.ballManager.update(deltaTime);
    
    // Update camera controller
    if (this.cameraController) {
      this.cameraController.update(deltaTime);
    }
  }
  
  /**
   * Process player shoot action
   */
  public processPlayerShoot(targetBasket: THREE.Vector3): void {
    eventBus.publish('player:shoot', { targetBasket });
  }
  
  /**
   * Process player block action
   */
  public processPlayerBlock(): void {
    eventBus.publish('player:block');
  }
  
  /**
   * Process player pass action
   */
  public processPlayerPass(targetX: number, targetZ: number): void {
    eventBus.publish('player:pass', { targetX, targetZ });
  }
  
  /**
   * Move the player
   */
  public movePlayer(velocityX: number, velocityZ: number): void {
    eventBus.publish('player:move', { velocityX, velocityZ });
  }
  
  /**
   * Pick up the ball
   */
  public pickupBall(): void {
    this.playerManager.pickupBall();
  }
  
  /**
   * Release the ball
   */
  public releaseBall(): void {
    this.playerManager.releaseBall();
  }
  
  /**
   * Return the ball to the player
   */
  public returnBallToPlayer(): void {
    this.playerManager.returnBallToPlayer();
  }
  
  /**
   * Toggle debug meshes
   */
  public toggleDebugMeshes(visible: boolean): void {
    eventBus.publish('debug:toggle-rigid-bodies', visible);
  }
  
  /**
   * Toggle debug visualizations
   */
  public toggleDebugVisualizations(visible?: boolean): void {
    eventBus.publish('debug:toggle-visualizations', visible);
  }
  
  /**
   * Clean up all resources
   */
  public dispose(): void {
    // Dispose all component managers
    this.courtManager.dispose();
    this.playerManager.dispose();
    this.ballManager.dispose();
    this.debugManager.dispose();
    this.uiManager.dispose();
    
    // Clear all event bus listeners
    eventBus.clearAllListeners();
  }
  
  // Getters needed for camera controller
  
  public getPlayerPosition(): THREE.Vector3 | null {
    return this.playerManager.getPlayerPosition();
  }
  
  public getPlayerRotation(): THREE.Euler | null {
    return this.playerManager.getPlayerRotation();
  }
  
  public playerHasBall(): boolean {
    return this.playerManager.playerHasBall();
  }
  
  public getBallPosition(): THREE.Vector3 | null {
    return this.ballManager.getBallPosition();
  }
  
  public getCourtDimensions(): { width: number; length: number } | null {
    return this.courtManager.getCourtDimensions();
  }
  
  public getHoopAPosition(): THREE.Vector3 {
    return this.courtManager.getHoopAPosition();
  }
  
  public getHoopBPosition(): THREE.Vector3 {
    return this.courtManager.getHoopBPosition();
  }
  
  public getClosestTeammatePosition(position: THREE.Vector3): THREE.Vector3 | null {
    return this.playerManager.getClosestTeammatePosition(position);
  }
  
  /**
   * Register animations with all players
   */
  public registerAnimations(): void {
    this.playerManager.registerAnimations();
  }
  
  /**
   * Update player configurations
   */
  public updatePlayerConfigs(): void {
    this.playerManager.updatePlayerConfigs();
  }
  
  /**
   * Get a rigid body by ID
   */
  public getRigidBody(id: string): THREE.Mesh | undefined {
    return this.debugManager.getRigidBody(id);
  }
  
  /**
   * Check if any player has the ball
   */
  private anyPlayerHasBall(): boolean {
    return this.ballManager.anyPlayerHasBall();
  }
  
  /**
   * Toggle ball dribble animation
   */
  public toggleDribbleAnimation(): void {
    if (this.playerManager) {
      if (this.isDribbling) {
        this.playerManager.stopDribbleAnimation();
        this.isDribbling = false;
      } else {
        this.playerManager.triggerDribbleAnimation();
        this.isDribbling = true;
      }
    }
  }

  /**
   * Get all players
   */
  public getAllPlayers(): Player[] {
    return this.playerManager.getAllPlayers();
  }

  /**
   * Get the local player
   */
  public getLocalPlayer(): Player | null {
    return this.playerManager.getLocalPlayer();
  }

  /**
   * Get the THREE.Scene instance
   */
  public getScene(): THREE.Scene {
    return this.scene;
  }

  /**
   * Get all player meshes in the scene
   */
  public getPlayerMeshes(): THREE.Object3D[] {
    const players = this.getAllPlayers();
    const meshes: THREE.Object3D[] = [];
    
    for (const player of players) {
      const mesh = player.getCharacterMesh();
      if (mesh) {
        meshes.push(mesh);
      }
    }
    
    return meshes;
  }

  /**
   * Find a player by their mesh or any child of their mesh
   */
  public getPlayerByMesh(mesh: THREE.Object3D): Player | null {
    const players = this.getAllPlayers();
    
    for (const player of players) {
      const playerMesh = player.getCharacterMesh();
      if (playerMesh) {
        // Check if the mesh is the player's mesh or a child of it
        if (mesh === playerMesh || this.isDescendant(mesh, playerMesh)) {
          return player;
        }
      }
    }
    
    return null;
  }

  /**
   * Helper method to check if a mesh is a descendant of another mesh
   */
  private isDescendant(child: THREE.Object3D, parent: THREE.Object3D): boolean {
    let current = child.parent;
    while (current) {
      if (current === parent) {
        return true;
      }
      current = current.parent;
    }
    return false;
  }
}